<template>
  <div>
    <div
      class="dropdown"
      :class="{
        'is-active': isOpen,
        'is-up': isUp,
        'is-left': direction === 'left',
        'is-right': direction === 'right',
      }"
      :style="{ top: getTopDistance() }"
    >
      <div class="dropdown-trigger" @click="togglePicker" v-click-away="closePicker" v-if="showButton">
        <button class="button">
          <span class="icon" v-if="isFlowVariableSelected()">
            <Icon icon="FlowVariableIcon" />
          </span>
          <span class="icon" v-if="isAppUserVariableSelected()">
            <Icon :icon="getIconComponent(selected)" />
          </span>
          <span>{{ buttonLabel }}</span>
          <span class="icon">
            <font-awesome-icon icon="angle-down"></font-awesome-icon>
          </span>
        </button>
      </div>
      <div class="dropdown-menu" id="dropdown-menu" role="menu">
        <div class="dropdown-content">
          <div class="scrollable-item-list">
            <div class="dropdown-item item-group-heading">
              <span>Contact</span>
            </div>
            <a
              @click="chooseVariable(variable)"
              class="dropdown-item"
              v-for="variable in contactVariables"
              :key="variable.id"
            >
              <Icon :icon="getIconComponent(variable)" />
              <span class="item-title">{{ variable.name }}</span>
            </a>
            <a id="add-attribute" v-if="!readonly" @click="openNewAttributeModal" class="dropdown-item add-item">
              <AddIcon />
              <span class="item-title">Add new attribute</span>
            </a>

            <hr v-if="!readonly || flowVariables.length > 0" class="dropdown-divider" />

            <div v-if="!readonly || flowVariables.length > 0" class="dropdown-item item-group-heading">
              <span>Flow</span>
            </div>
            <a
              @click="chooseVariable(variable)"
              class="dropdown-item"
              v-for="variable in flowVariables"
              :key="variable.id"
            >
              <Icon icon="FlowVariableIcon" />
              <span class="item-title">{{ variable.name }}</span>
            </a>
            <a v-if="!readonly" id="add-variable" @click="openNewVariableModal" class="dropdown-item add-item">
              <AddIcon />
              <span class="item-title">Add new variable</span>
            </a>
          </div>
        </div>
      </div>
      <div v-if="!showButton && isOpen" :class="{ 'arrow-down': isUp, 'arrow-up': !isUp }">
        <div class="inner-arrow"></div>
      </div>
    </div>
    <AddNewVariableModal v-if="showNewVariableModal" @added="variableAdded" @close="closeModal" />
    <CreateAttribute
      v-if="showCreateAttributeModal"
      :excluded-data-types="[ContactAttributeType.Date]"
      @cancel="closeAttributeModel"
      @create="closeAttributeModel"
    />
  </div>
</template>

<script lang="ts" setup>
import { InputVariableViewModel } from '@/features/bots/models/designer';
import AddNewVariableModal from './AddNewVariableModal.vue';
import { dropDownHelper } from '@/shared/components/dropDownMenu/dropDownHelper';
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import Icon from '@/shared/icons/Icon.vue';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import AttributeIconUtil from '@/shared/util/attributeIconUtil';
import { ContactAttributeType } from '@/messaging/models/contactAttributeType';
import CreateAttribute from '@/features/settings/components/CreateAttribute.vue';
import { computed, nextTick, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  value: {
    type: Object as PropType<InputVariableViewModel>,
  },
  showButton: {
    type: Boolean,
    default: true,
  },
  direction: {
    type: String as PropType<'left' | 'right'>,
    default: 'right',
  },
  open: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  additionalAttributes: {
    type: Array as PropType<InputVariableViewModel[]>,
    default: [],
  },
});

const emit = defineEmits<{
  (e: 'variable-selected', value: InputVariableViewModel): void;
}>();

const store = useStore();
const flowVariables = computed(() => store.getters.flowVariables);
const contactAttributes = computed<ContactAttributeViewModel[]>(() => store.getters.contactAttributes);
const getAttribute = (attributeId: string) => computed(() => store.getters.getAttribute(attributeId));

const selected = ref<InputVariableViewModel>({
  id: '',
  key: '',
  name: '',
  scope: '',
});
const isOpen = ref(false);
const showNewVariableModal = ref(false);
const showCreateAttributeModal = ref(false);
const isUp = ref(false);

const contactVariables = computed(() => {
  const filtered = contactAttributes.value
    .filter((attr: ContactAttributeViewModel) => attr.type !== ContactAttributeType.Date)
    .map(
      (attr: ContactAttributeViewModel) =>
        ({
          id: (attr.system ? 'appUser.' : 'appUser.customAttributes.') + attr.id,
          scope: attr.system ? 'appUser' : 'appUser.customAttributes',
          key: attr.id,
          name: attr.name,
          dataType: attr.type,
        }) as InputVariableViewModel,
    );

  if (props.additionalAttributes) {
    filtered.unshift(...props.additionalAttributes);
  }
  return filtered;
});

watch(
  () => props.value,
  (value: InputVariableViewModel | undefined) => (selected.value = value || ({} as InputVariableViewModel)),
  { immediate: true },
);

watch(
  () => props.open,
  (value: boolean) => {
    isOpen.value = value;
    positionDropdown();
  },
  { immediate: true },
);

function getIconComponent(variable: InputVariableViewModel) {
  return AttributeIconUtil.getIconType(variable.dataType, variable.key, isSystemAttribute(variable.key));
}

function isFlowVariableSelected() {
  return selected.value?.scope === 'flow';
}

function isAppUserVariableSelected() {
  return selected.value?.scope && selected.value.scope.startsWith('appUser');
}

const buttonLabel = computed(() => selected.value?.name || 'Choose a variable...');

function chooseVariable(variable: InputVariableViewModel) {
  selected.value = variable;
  emit('variable-selected', selected.value);
}

function closePicker() {
  isOpen.value = false;
  isUp.value = false;
}

function togglePicker() {
  isOpen.value = !isOpen.value;
  positionDropdown();
}

function variableAdded(newVar: InputVariableViewModel) {
  chooseVariable(newVar);
  closeModal();
}

function openNewVariableModal() {
  showNewVariableModal.value = true;
}

function closeModal() {
  showNewVariableModal.value = false;
}

function getTopDistance() {
  if (isUp.value && !props.showButton) {
    return '-27px';
  } else if (!isUp.value && !props.showButton) {
    return '4px';
  }
  return '0px';
}

function openNewAttributeModal() {
  showCreateAttributeModal.value = true;
}

function closeAttributeModel(newAttribute?: ContactAttributeViewModel) {
  showCreateAttributeModal.value = false;
  if (newAttribute) {
    const variableForNewAttribute = contactVariables.value.find(
      (attr: InputVariableViewModel) => attr.key === newAttribute.id,
    );
    if (variableForNewAttribute && variableForNewAttribute.dataType !== ContactAttributeType.Date) {
      chooseVariable(variableForNewAttribute);
    }
  }
}

function isSystemAttribute(variableId: string) {
  const attribute = getAttribute(variableId).value;
  return attribute ? attribute.system : false;
}

function positionDropdown() {
  nextTick(() => (isUp.value = dropDownHelper.isDropDownUp()));
}
</script>

<style scoped lang="scss">
.button {
  background-color: transparent;
  border: solid 1px transparent;
  color: $color-netcall-body-text;

  &:hover {
    color: $color-netcall-body-text;
    border-color: $color-netcall-body-text;
  }

  &:focus {
    color: $color-netcall-body-text;
    border-color: $color-netcall-body-text;
  }

  &:focus:not(:active) {
    box-shadow: 0 0 0 0.125em rgba($color-netcall-body-text, 0.25);
  }
}

.dropdown-divider {
  background-color: #ededed;
  border: none;
  display: block;
  height: 1px;
  margin: 0.5rem 0;
}

.item-group-heading {
  font-weight: bold;
  font-size: small;
}

.scrollable-item-list {
  max-height: 250px;
  overflow-y: auto;
}

.dropdown-item {
  color: $color-netcall-body-text;

  &.add-item {
    color: $color-netcall-deep-sea;
  }
}

.item-title {
  padding-left: 0.75em;
}
.arrow-up {
  top: -6px;
  position: absolute;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid #dadada;
  bottom: -1px;
  z-index: 9999;
  .inner-arrow {
    position: relative;
    width: 0;
    height: 0;
    top: 1.5px;
    right: 10px;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid rgb(255, 255, 255);
  }
}
.arrow-down {
  top: -4px;
  position: absolute;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid #dadada;
  z-index: 9999;

  .inner-arrow {
    position: relative;
    width: 0;
    height: 0;
    bottom: 11.5px;
    right: 10px;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid rgb(255, 255, 255);
  }
}
</style>
